<template>
  <div v-if="!isShowAccountList || !isShowAccountRole" class="login-form">
    <md-tabs v-model="activeName" size="default">
      <md-tab-pane label="密码登录" name="password"><pwd-login /></md-tab-pane>
      <!-- <md-tab-pane label="短信登录" name="mobile"><sms-login /></md-tab-pane> -->
    </md-tabs>
  </div>
  <account-list
    v-if="isShowAccountList"
    ref="accountComponent"
    :style="{ display: isShowAccountRole ? 'block' : 'none' }"
    :login-mode="loginMode"
    :account-data="accountData"
    :account-role-datas="accountRoleDatas"
    :account-params="accountParams"
    :storage-resource-params="storageResourceParams"
    :handle-login-fn="loginFn"
    @onBackLogin="backLogin"
    @showAccountList="showAccountList"
  />
</template>
<script setup lang="ts">
import { ref, provide } from 'vue';
import { MdTabs, MdTabPane, MdMessage } from 'mdesign3';
import PwdLogin from './pwdLogin.vue';
import SmsLogin from './smsLogin.vue';
import AccountList from './accountList.vue';
import config from '@/config/user.env';
import { loginBySms, getUserRole } from '@/api/userApi';
import { storageResource } from './utilsLogin';
import { ILoginBySmsParams, IStorageResourceParams } from '@/utils/interface';
const activeName = ref<string>('password');
const isShowAccountList = ref<Boolean>(false);
const isShowAccountRole = ref<Boolean>(false);
const accountRoleDatas = ref<Array<any>>([]);
const accountData = ref<Array<any>>([]);
const accountParams = ref<any>({});
const loginMode = ref<string>('');
const warningMsg = ref<string>('');
const storageResourceParams = ref<IStorageResourceParams>({});
const accountComponent = ref<InstanceType<typeof ChildComponent> | null>(null);

// 合并接口
type ILoginResourceParams = ILoginBySmsParams & IStorageResourceParams;
// 短信登录获取角色逻辑处理
const getUserRoleFn = (receiveData: any) => {
  const { accountByData, accountByParams, msg } = receiveData;
  warningMsg.value = msg;
  if (accountByData && accountByData.length > 0) {
    const userCodeArr: Array<string> = accountByData.map((item) => item.userCode);
    const userCodes = userCodeArr.toString();
    const applicationCode = config.applicationCode;
    const param = {
      userCodes,
      positionCode: 'main',
      applicationCode,
    };
    let accountRoles: Array<any> = [];
    getUserRole(param).then((res) => {
      if (res.code === '0' && res.data.length === 0) {
        MdMessage.error('获取用户角色失败，请联系系统管理员');
      }
      if (res.code === '0' && res.data.length > 0) {
        const { data } = res;
        for (let i = 0; i < data.length; i++) {
          const item = data[i];
          const roles = accountByData.find((role) => role.userCode === item.userCode);
          if (roles) {
            const obj = {
              applicationCode: item.applicationCode,
              applicationName: item.applicationName,
              roleCode: item.roleCode,
              roleName: item.roleName,
              tenantCode: item.tenantCode,
            };
            accountRoles.push({ ...roles, ...obj });
          }
        }
        accountRoleDatas.value = accountRoles;
        isShowAccountList.value = true;
        accountParams.value = accountByParams;
        if (accountComponent.value) {
          accountComponent.value.getControlParamFn();
        }
      }
    });
  }
};

// 短信登录：仅写入登录态，预初始化统一由 accountList.navigateAfterLogin 处理
const loginFn = (params: ILoginResourceParams, callback: (err?: unknown) => void) => {
  const { mobile = '', verificationCode = '', userCode = '', roleCode, roleList } = params;
  const data: ILoginBySmsParams = {
    mobile,
    verificationCode,
    userCode,
  };
  loginBySms(data)
    .then(async (res) => {
      if (res.code === '0') {
        try {
          const userInfo = res.data[0];
          await storageResource({ roleCode, roleList, userInfo, currentTenant: userInfo.currentTenant });
          callback();
        } catch (err) {
          MdMessage.error('登录信息保存失败');
          callback(err);
        }
      } else {
        MdMessage.error(res.msg || '短信登录失败');
        callback(new Error(res.msg || '短信登录失败'));
      }
    })
    .catch(() => {
      MdMessage.error('短信登录失败');
      callback(new Error('短信登录失败'));
    });
};

// 接收短信登录和账号登录数据
const receiveAccountData = (receiveData: any) => {
  const { mode, accountByData } = receiveData;
  loginMode.value = mode;
  if (loginMode.value === 'SMS') {
    accountData.value = accountByData;
    getUserRoleFn(receiveData);
  } else {
    storageResourceParams.value = receiveData?.storageResourceParams;
    isShowAccountList.value = true;
    isShowAccountRole.value = true;
  }
};

const backLogin = () => {
  isShowAccountList.value = false;
  isShowAccountRole.value = false;
};

const showAccountList = () => {
  isShowAccountList.value = true;
  isShowAccountRole.value = true;
  MdMessage({
    message: warningMsg.value,
    type: 'warning',
  });
};

provide('receiveAccountData', receiveAccountData);
</script>

<style scoped lang="less">
.login-form {
  width: 320px;
  height: 400px;
  padding: 24px;
  margin-right: 6vw;
  background-color: #fff;
  border-radius: 4px;
  box-sizing: border-box;
  box-shadow: 0px 2px 8px 0px rgba(0, 21, 41, 0.06);
  :deep(.md-tabs__content) {
    overflow: visible;
  }
  :deep(.md-tabs__header) {
    margin-bottom: 32px;
  }
  :deep(.md-input__inner) {
    background-color: #f2f3f5;
    border-color: #f2f3f5;
    border-radius: 4px;
    &.md-input__inner:hover {
      border-color: #e5e6eb;
      background-color: #e5e6eb;
    }
    &.md-input__inner:focus {
      border-color: var(--md-input-focus-border, var(--md-color-primary));
    }
  }
  :deep(.md-tabs__item) {
    height: 44px;
    line-height: 44px;
    font-weight: 400;
    &.is-active {
      font-weight: 500;
    }
  }
  :deep(.md-form-item) {
    margin-bottom: 24px;
    .md-input--large {
      font-size: 14px;
    }
    &.is-error {
      .md-input__inner {
        background-color: #fee9e7;
        border-color: var(--md-color-danger);
      }
    }
    &.password-item {
      margin-bottom: 16px;
    }
  }
  :deep(.md-checkbox__label) {
    color: #86909c;
    font-weight: 400;
  }
  :deep(.md-checkbox) {
    .md-checkbox__inner {
      position: relative;
      top: 1px;
      width: 14px;
      height: 14px;
      border-color: #c0c5cf;
      .md-checkbox.md-checkbox--small .md-checkbox__inner::after {
        width: 3px;
        height: 6px;
        top: 0.5px;
        left: 3.5px;
      }
    }
    .md-checkbox__input.is-checked .md-checkbox__inner {
      border-color: var(--md-checkbox-checked-input-border-color);
    }
    &:hover {
      .md-checkbox__input .md-checkbox__inner {
        border-color: var(--md-checkbox-checked-input-border-color);
      }
    }
  }
  :deep(.md-tabs__nav-wrap::after) {
    background-color: #f2f3f5;
  }
  :deep(.agreement) {
    position: relative;
    top: -2px;
    font-weight: 400;
    color: var(--md-color-primary);
  }
  :deep(.submit-btn) {
    display: block;
    width: 100%;
    padding: 0;
    margin: 25px 0 9px;
    border-radius: 4px;
    line-height: 40px;
    > span {
      position: relative;
      top: -1px;
    }
  }
  :deep(.register-btn) {
    display: flex;
    justify-content: flex-end;
    margin-top: 16px;
  }
  :deep(.md-input__suffix-inner) {
    position: relative;
    padding-left: 12px;
    .md-button--text {
      font-size: 14px;
    }
    .verify-code-btn::before {
      content: '';
      position: absolute;
      left: 0;
      width: 1px;
      height: 14px;
      background-color: #c0c5cf;
    }
  }
}
</style>
